import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TextField,
  Stack,
  Typography,
  IconButton,
  LinearProgress,
  Box,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  BugReport as JiraIcon,
  Palette as FigmaIcon
} from '@mui/icons-material';
import API_CONFIG from '../config/api';
import { SV } from '../automation/stratovaiTestIds';

const SUPPORTED_PRD_UPLOAD_EXTENSIONS = ['.txt', '.md', '.markdown', '.pdf'];
const SUPPORTED_PRD_UPLOAD_COPY = 'Plain text (.txt), Markdown (.md, .markdown), and PDF (.pdf)';

const getUnsupportedPrdUploadMessage = (fileName) => {
  const suffix = fileName?.includes('.') ? fileName.slice(fileName.lastIndexOf('.')).toLowerCase() : 'unknown';
  return `Unsupported file type "${suffix}". Product Knowledge uploads currently support ${SUPPORTED_PRD_UPLOAD_COPY} only. Convert the document to plain text or Markdown, or paste the content directly, then try again.`;
};

const ProductKnowledgeAdd = ({ selectedProject, onUploadFile, onPasteText, onJiraImport, onFigmaIngestCompleted }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [jiraInput, setJiraInput] = useState('');
  const [figmaUrl, setFigmaUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jiraStatus, setJiraStatus] = useState({ loading: false, message: '', error: false });
  const [figmaStatus, setFigmaStatus] = useState({ loading: false, message: '', error: false });
  const [figmaPlan, setFigmaPlan] = useState(null);
  const [figmaConflictOpen, setFigmaConflictOpen] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Preparing import...');
  const [uploadError, setUploadError] = useState('');
  const progressTimerRef = useRef(null);
  const messageTimerRef = useRef(null);
  const figmaJobPollRef = useRef(null);

  const wittyMessages = useMemo(() => ([
    'Connecting to JIRA... brewing coffee ☕',
    'Extracting Figma URLs like a detective 🕵️',
    'Syncing frames and downloading screenshots 📸',
    'Running semantic understanding... AI is thinking 🧠',
    'Classifying components and inferring flows 🔄',
    'Extracting business rules from designs 📋',
    'Indexing everything into the knowledge base 📚',
    'Almost there... polishing the details ✨',
    'Finalizing import... almost done! 🎉'
  ]), []);
  
  // Title is required for file upload and paste, but optional for JIRA import
  const canSubmit = (
    tab === 0 ? (title.trim().length > 0 && !!file) : 
    tab === 1 ? (title.trim().length > 0 && text.trim().length > 0) : 
    tab === 2 ? jiraInput.trim().length > 0 :
    tab === 3 ? (title.trim().length > 0 && figmaUrl.trim().length > 0) : false
  );

  const reset = () => {
    setTitle('');
    setFile(null);
    setText('');
    setJiraInput('');
    setFigmaUrl('');
    setTab(0);
    setJiraStatus({ loading: false, message: '', error: false });
    setFigmaStatus({ loading: false, message: '', error: false });
    setFigmaPlan(null);
    setFigmaConflictOpen(false);
    setShowLoadingModal(false);
    setLoadingProgress(0);
    setLoadingMessage('Preparing import...');
    setUploadError('');
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (messageTimerRef.current) clearInterval(messageTimerRef.current);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (messageTimerRef.current) clearInterval(messageTimerRef.current);
      if (figmaJobPollRef.current) clearInterval(figmaJobPollRef.current);
    };
  }, []);

  const stopFigmaJobPolling = () => {
    if (figmaJobPollRef.current) clearInterval(figmaJobPollRef.current);
    figmaJobPollRef.current = null;
  };

  const pollFigmaJobUntilDone = async (jobId) => {
    stopFigmaJobPolling();
    return await new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const res = await API_CONFIG.request(`/api/figma/jobs/${encodeURIComponent(jobId)}`, { method: 'GET' });
          const json = await res.json();
          if (!res.ok) return;
          const status = json?.status;
          const progress = Number(json?.progress ?? 0);
          const message = json?.message || (status === 'running' ? 'Running…' : 'Queued…');
          setLoadingProgress(Number.isFinite(progress) ? progress : 0);
          setLoadingMessage(message);

          if (status === 'succeeded') {
            stopFigmaJobPolling();
            resolve(json?.result || { job_id: jobId });
          }
          if (status === 'failed' || status === 'cancelled') {
            stopFigmaJobPolling();
            reject(new Error(json?.error || 'Figma ingestion job failed'));
          }
        } catch (e) {
          // keep polling
        }
      };

      figmaJobPollRef.current = setInterval(poll, 2500);
      poll();
    });
  };

  const startLoadingModal = (initialMessage) => {
    setShowLoadingModal(true);
    setLoadingProgress(0);
    setLoadingMessage(initialMessage || wittyMessages[0]);

    let progress = 0;
    let messageIndex = 0;

    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (messageTimerRef.current) clearInterval(messageTimerRef.current);

    progressTimerRef.current = setInterval(() => {
      progress += Math.random() * 15;
      if (progress > 90) progress = 90; // cap until completion
      setLoadingProgress(progress);
    }, 500);

    messageTimerRef.current = setInterval(() => {
      messageIndex = (messageIndex + 1) % wittyMessages.length;
      setLoadingMessage(wittyMessages[messageIndex]);
    }, 6000);
  };

  const stopLoadingModal = (finalMessage, isSuccess = true) => {
    setLoadingProgress(100);
    if (finalMessage) setLoadingMessage(finalMessage);
    setTimeout(() => {
      setShowLoadingModal(false);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (messageTimerRef.current) clearInterval(messageTimerRef.current);
    }, isSuccess ? 1000 : 0);
  };

  const runFigmaPlan = async () => {
    const projectId = selectedProject?.project_id || selectedProject?.id;
    if (!projectId) {
      throw new Error('Please select a project.');
    }
    const res = await API_CONFIG.request(`/api/figma/ingest-direct/plan`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        figma_url: figmaUrl.trim(),
        title: title.trim()
      })
    });
    const json = await res.json();
    if (!res.ok || !json?.success) {
      throw new Error(json?.detail || json?.message || `Figma plan failed (HTTP ${res.status})`);
    }
    return json.plan;
  };

  const runFigmaExecute = async (action) => {
    const projectId = selectedProject?.project_id || selectedProject?.id;
    if (!projectId) {
      throw new Error('Please select a project.');
    }
    const res = await API_CONFIG.request(`/api/figma/ingest-direct/execute`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        figma_url: figmaUrl.trim(),
        title: title.trim(),
        action
      })
    });
    const json = await res.json();
    if (!res.ok || !json?.success) {
      throw new Error(json?.detail || json?.message || `Figma ingest failed (HTTP ${res.status})`);
    }
    return json.result;
  };

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] ?? null;
    setUploadError('');

    if (!nextFile) {
      setFile(null);
      return;
    }

    const lowerName = nextFile.name.toLowerCase();
    const isSupported = SUPPORTED_PRD_UPLOAD_EXTENSIONS.some((extension) => lowerName.endsWith(extension));

    if (!isSupported) {
      setFile(null);
      setUploadError(getUnsupportedPrdUploadMessage(nextFile.name));
      event.target.value = '';
      return;
    }

    setFile(nextFile);
    if (!title.trim()) {
      setTitle(nextFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setUploadError('');
    try {
      if (tab === 0 && file) {
        await onUploadFile({ title: title.trim(), file });
      }
      if (tab === 1) {
        await onPasteText({ title: title.trim(), text: text.trim() });
      }
      if (tab === 2) {
        // Show loading modal with witty messages
        startLoadingModal(wittyMessages[0]);
        
        try {
          const payload = { input: jiraInput.trim() };
          const t = title.trim();
          if (t.length > 0) payload.title = t; // include only if explicitly provided
          await onJiraImport(payload);
          
          // Complete the progress
          stopLoadingModal('Import completed successfully! 🎉', true);
          
          setJiraStatus({ loading: false, message: 'Successfully imported from JIRA!', error: false });
        } catch (error) {
          stopLoadingModal(null, false);
          setJiraStatus({ loading: false, message: error.message || 'Failed to import from JIRA', error: true });
          throw error;
        }
      }
      if (tab === 3) {
        setFigmaStatus({ loading: true, message: 'Checking Figma link...', error: false });
        const plan = await runFigmaPlan();
        setFigmaPlan(plan);

        if (plan?.requires_confirmation) {
          // Always prompt on overlaps/reimport (Decision 2)
          setFigmaConflictOpen(true);
          setFigmaStatus({ loading: false, message: '', error: false });
          return; // wait for user choice
        }

        // No conflicts – execute directly
        startLoadingModal('Connecting to Figma...');
        const result = await runFigmaExecute(null);
        if (result?.job_id) {
          // Switch from simulated progress to real job progress
          if (progressTimerRef.current) clearInterval(progressTimerRef.current);
          if (messageTimerRef.current) clearInterval(messageTimerRef.current);
          setLoadingProgress(0);
          setLoadingMessage('Queued…');
          const finalResult = await pollFigmaJobUntilDone(result.job_id);
          stopLoadingModal('Figma ingestion completed!', true);
          setFigmaStatus({ loading: false, message: 'Figma knowledge ingested (staged).', error: false });
          if (onFigmaIngestCompleted) {
            await onFigmaIngestCompleted(finalResult);
          }
        } else {
          stopLoadingModal('Figma ingestion completed!', true);
          setFigmaStatus({ loading: false, message: 'Figma knowledge ingested (staged).', error: false });
          if (onFigmaIngestCompleted) {
            await onFigmaIngestCompleted(result);
          }
        }
      }
      setOpen(false);
      reset();
    } catch (error) {
      if (tab === 0 || tab === 1) {
        setUploadError(error.message || 'Failed to add Product Knowledge');
      }
      if (tab === 2) {
        setJiraStatus({ loading: false, message: error.message || 'Failed to import from JIRA', error: true });
      }
      if (tab === 3) {
        setFigmaStatus({ loading: false, message: error.message || 'Failed to ingest from Figma', error: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFigmaConflictAction = async (action) => {
    try {
      setSubmitting(true);
      startLoadingModal('Processing Figma ingestion...');
      const result = await runFigmaExecute(action);
      if (result?.job_id) {
        if (progressTimerRef.current) clearInterval(progressTimerRef.current);
        if (messageTimerRef.current) clearInterval(messageTimerRef.current);
        setLoadingProgress(0);
        setLoadingMessage('Queued…');
        const finalResult = await pollFigmaJobUntilDone(result.job_id);
        stopLoadingModal('Figma ingestion completed!', true);
        if (onFigmaIngestCompleted) {
          await onFigmaIngestCompleted(finalResult);
        }
      } else {
        stopLoadingModal('Figma ingestion completed!', true);
        if (onFigmaIngestCompleted) {
          await onFigmaIngestCompleted(result);
        }
      }
      setFigmaConflictOpen(false);
      setFigmaPlan(null);
      setFigmaStatus({ loading: false, message: 'Figma knowledge updated (staged).', error: false });

      setOpen(false);
      reset();
    } catch (e) {
      stopLoadingModal(null, false);
      setFigmaStatus({ loading: false, message: e.message || 'Failed to ingest from Figma', error: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        data-testid={SV.pk.addKnowledgeOpen}
        onClick={() => setOpen(true)}
        sx={{ 
          borderRadius: 3,
          px: 3,
          py: 1,
          fontWeight: 600,
          boxShadow: 2,
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-1px)'
          }
        }}
      >
        Add Knowledge
      </Button>

      <Dialog 
        open={open} 
        onClose={() => !submitting && setOpen(false)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{ 'data-testid': SV.pk.addDialog }}
      >
        <DialogTitle component="div" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Product Knowledge
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Upload PRD docs or paste notes to add to Stratovai's knowledge base.
            </Typography>
          </Box>
          <IconButton 
            onClick={() => !submitting && setOpen(false)} 
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {submitting && <LinearProgress />}

        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={tab === 2 ? "Title / Name (Optional)" : "Title / Name"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={tab === 2 ? "Leave empty to use JIRA title" : "e.g., Login Page PRD"}
              fullWidth
              autoFocus
              disabled={submitting}
              required={tab !== 2}
              helperText={tab === 2 ? "If empty, the JIRA issue title will be used automatically" : ""}
              inputProps={{ 'data-testid': SV.pk.addTitleField }}
            />

            <Tabs 
              data-testid={SV.pk.addTabs}
              value={tab} 
              onChange={(_, v) => { if (v === 2) setTitle(''); setTab(v); }} 
              aria-label="add-knowledge-tabs"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.95rem'
                },
                '& .Mui-selected': {
                  fontWeight: 600,
                  color: 'primary.main'
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '2px 2px 0 0'
                }
              }}
            >
              <Tab label="Upload File" data-testid={SV.pk.addTabUpload} />
              <Tab label="Paste Text" data-testid={SV.pk.addTabPaste} />
              <Tab 
                label="JIRA/Confluence"
                data-testid={SV.pk.addTabJira}
                icon={<JiraIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Figma"
                data-testid={SV.pk.addTabFigma}
                icon={<FigmaIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={1.5}>
                <label htmlFor="pkb-file-input">
                  <input
                    id="pkb-file-input"
                    data-testid={SV.pk.addFileInput}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={submitting}
                    accept=".txt,.md,.markdown,.pdf,text/plain,text/markdown,application/pdf"
                  />
                  <Button 
                    variant="outlined" 
                    component="span"
                    data-testid={SV.pk.addBrowseFile}
                    disabled={submitting}
                  >
                    {file ? 'Change file' : 'Browse file'}
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary">
                  Supported: {SUPPORTED_PRD_UPLOAD_COPY}
                </Typography>
                {file && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                      📄
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(file.size/1024)} KB
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            )}

            {tab === 1 && (
              <TextField
                label="Content"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste PRD or notes here…"
                minRows={8}
                maxRows={20}
                multiline
                fullWidth
                disabled={submitting}
                inputProps={{ 'data-testid': SV.pk.addPasteContent }}
              />
            )}

            {uploadError && tab !== 2 && tab !== 3 && (
              <Alert severity="error">
                {uploadError}
              </Alert>
            )}

            {tab === 2 && (
              <Stack spacing={2}>
                <TextField
                  label="JIRA Issue/Project/Query"
                  value={jiraInput}
                  onChange={(e) => setJiraInput(e.target.value)}
                  placeholder="Enter JIRA issue key (STRD-12), project key (STRD), or JQL query..."
                  fullWidth
                  disabled={submitting}
                  helperText="Smart input: Automatically detects single issues, projects, or JQL queries"
                  inputProps={{ 'data-testid': SV.pk.addJiraInput }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label="Single Issue" 
                    size="small" 
                    variant="outlined"
                    onClick={() => setJiraInput('STRD-12')}
                    disabled={submitting}
                    color="primary"
                  />
                  <Chip 
                    label="Entire Project" 
                    size="small" 
                    variant="outlined"
                    onClick={() => setJiraInput('STRD')}
                    disabled={submitting}
                    color="secondary"
                  />
                  <Chip 
                    label="My Tasks" 
                    size="small" 
                    variant="outlined"
                    onClick={() => setJiraInput("assignee = currentUser()")}
                    disabled={submitting}
                    color="info"
                  />
                  <Chip 
                    label="High Priority" 
                    size="small" 
                    variant="outlined"
                    onClick={() => setJiraInput("priority = 'High'")}
                    disabled={submitting}
                    color="warning"
                  />
                  <Chip 
                    label="Recent Issues" 
                    size="small" 
                    variant="outlined"
                    onClick={() => setJiraInput("created >= -7d")}
                    disabled={submitting}
                    color="success"
                  />
                </Box>

                {jiraStatus.loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {jiraStatus.message}
                    </Typography>
                  </Box>
                )}

                {jiraStatus.message && !jiraStatus.loading && (
                  <Alert 
                    severity={jiraStatus.error ? 'error' : 'success'}
                    sx={{ mt: 1 }}
                  >
                    {jiraStatus.message}
                  </Alert>
                )}
              </Stack>
            )}

            {tab === 3 && (
              <Stack spacing={2}>
                <TextField
                  label="Figma URL"
                  value={figmaUrl}
                  onChange={(e) => setFigmaUrl(e.target.value)}
                  placeholder="https://www.figma.com/file/... or ...?node-id=..."
                  fullWidth
                  disabled={submitting}
                  helperText="Paste a Figma file/canvas URL (composite) or a node-id URL (single frame/canvas subset)."
                  inputProps={{ 'data-testid': SV.pk.addFigmaUrl }}
                />

                {figmaStatus.loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {figmaStatus.message}
                    </Typography>
                  </Box>
                )}

                {figmaStatus.message && !figmaStatus.loading && (
                  <Alert
                    severity={figmaStatus.error ? 'error' : 'success'}
                    sx={{ mt: 1 }}
                  >
                    {figmaStatus.message}
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button 
            data-testid={SV.pk.addCancel}
            onClick={() => { setOpen(false); reset(); }} 
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            data-testid={
              tab === 0 ? SV.pk.addUploadSubmit
                : tab === 1 ? SV.pk.addPasteSubmit
                  : tab === 2 ? SV.pk.addJiraSubmit
                    : SV.pk.addFigmaSubmit
            }
            sx={{ 
              fontWeight: 600,
              px: 3
            }}
          >
            {tab === 0 ? 'Upload PRD' : 
             tab === 1 ? 'Save Notes' : 
             tab === 2 ? 'Import from JIRA' :
             'Ingest from Figma'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Figma Conflict Dialog (Decision 2: always prompt) */}
      <Dialog
        open={figmaConflictOpen}
        onClose={() => !submitting && setFigmaConflictOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-testid': SV.pk.figmaConflictDialog }}
      >
        <DialogTitle>
          {figmaPlan?.prompt_type === 'reimport' ? 'Re-import detected' :
           figmaPlan?.prompt_type === 'single_frame_conflict' ? 'Frame overlap detected' :
           figmaPlan?.prompt_type === 'composite_conflict' ? 'Overlap detected' :
           'Confirm action'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {figmaPlan?.existing_entry && (
              <Alert severity="info">
                This Figma link matches an existing entry: <strong>{figmaPlan.existing_entry.title}</strong> ({figmaPlan.existing_entry.status}).
                Re-import will overwrite it and move it to <strong>Staged</strong>.
              </Alert>
            )}

            {Array.isArray(figmaPlan?.overlaps) && figmaPlan.overlaps.length > 0 && (
              <Alert severity="warning">
                This ingestion overlaps frames already used in {figmaPlan.overlaps.length} other Figma entry(ies).
                Proceeding may require review of those designs.
              </Alert>
            )}

            {Array.isArray(figmaPlan?.overlaps) && figmaPlan.overlaps.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Impacted entries
                </Typography>
                <Stack spacing={1}>
                  {figmaPlan.overlaps.map((o) => (
                    <Box key={o.entry_id} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {o.title} <Typography component="span" variant="caption" color="text.secondary">({o.status})</Typography>
                      </Typography>
                      {Array.isArray(o.overlap_frames) && o.overlap_frames.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Overlap: {o.overlap_frames.slice(0, 6).map(f => f.frame_name || f.frame_id).join(', ')}
                          {o.overlap_frames.length > 6 ? '…' : ''}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {figmaPlan?.prompt_type === 'single_frame_conflict' && (
              <Alert severity="info">
                Choose whether to update the existing design(s) from source, or keep this as a separate staged entry.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFigmaConflictOpen(false)} disabled={submitting}>
            Cancel
          </Button>

          {figmaPlan?.prompt_type === 'single_frame_conflict' ? (
            <>
              <Button
                variant="outlined"
                onClick={() => handleFigmaConflictAction('update_existing')}
                disabled={submitting}
              >
                Update existing design(s)
              </Button>
              <Button
                variant="contained"
                onClick={() => handleFigmaConflictAction('keep_separate')}
                disabled={submitting}
              >
                Keep separate (create new)
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => handleFigmaConflictAction('proceed')}
              disabled={submitting}
            >
              Proceed
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* JIRA Import Loading Modal */}
      <Dialog 
        open={showLoadingModal} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          'data-testid': SV.pk.jiraFigmaLoadingDialog,
          sx: {
            borderRadius: 3,
            p: 2
          }
        }}
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {tab === 3 ? 'Processing Figma' : 'Importing from JIRA'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {loadingMessage}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a minute or two...
              </Typography>
            </Box>
            <Box>
              <LinearProgress 
                variant="determinate" 
                value={loadingProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4
                  }
                }} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {Math.round(loadingProgress)}%
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductKnowledgeAdd;
